import { useState, useEffect } from 'react';
import { Package, Plus, ShoppingCart, Check } from 'lucide-react';
import { formatPrice, calculateBuyPrice, cn, getValueTier, getDefaultEdition, getActivePrices, getActiveGradedPrices, getEditionFullName } from '../lib/utils';
import { getInventory } from '../lib/api';
import { track } from '../lib/telemetry';
import { PriceHistoryChart } from './PriceHistoryChart';
import { CardImage } from './CardImage';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import type { Card, Mode, InventoryItem, Edition, CartItem } from '../lib/types';

interface CardDetailPanelProps {
  card: Card;
  mode: Mode;
  buyPercent: number;
  onAddToCompare?: (card: Card) => void;
  comparisonCards?: Card[];
  onAddToCart?: (item: Omit<CartItem, 'id'>) => void;
}

interface ConditionPrice {
  condition: string;
  label: string;
  price: number | undefined;
}

// Quote-mode buy-percent stepper buttons. Mirrors CardDetail (phone) so the
// experience is consistent across phone and tablet form factors.
const QUOTE_PERCENT_OPTIONS = [100, 90, 85, 80, 75, 70];

export function CardDetailPanel({
  card,
  mode,
  buyPercent,
  onAddToCompare,
  comparisonCards = [],
  onAddToCart,
}: CardDetailPanelProps) {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(true);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const [selectedEdition, setSelectedEdition] = useState<Edition | null>(() =>
    getDefaultEdition(card)
  );
  const [selectedCondition, setSelectedCondition] = useState<string>('nm');
  const [addedConfirmation, setAddedConfirmation] = useState<string | null>(null);
  const [activePercent, setActivePercent] = useState(buyPercent);

  const isQuoteMode = !onAddToCart;
  const showQuoteStepper = isQuoteMode && mode === 'buy';

  // Get active prices based on selected edition
  const activePrices = getActivePrices(card, selectedEdition);
  const activeGradedPrices = getActiveGradedPrices(card, selectedEdition);

  // Check if card is already in comparison
  const isInComparison = comparisonCards.some((c) => c.id === card.id);
  const canAddToComparison = comparisonCards.length < 3 && !isInComparison;

  // Load inventory status
  useEffect(() => {
    async function loadInventory() {
      setIsLoadingInventory(true);
      setInventoryError(null);

      try {
        const response = await getInventory(card.set_name, card.card_number);
        setInventory(response.items || []);
      } catch (error) {
        console.error('Inventory load error:', error);
        setInventoryError('Unable to check stock');
        setInventory([]);
      } finally {
        setIsLoadingInventory(false);
      }
    }

    loadInventory();
  }, [card.set_name, card.card_number]);

  // Reset edition and condition when card changes
  useEffect(() => {
    setSelectedEdition(getDefaultEdition(card));
    setSelectedCondition('nm');
    setAddedConfirmation(null);
  }, [card.id]);

  const conditions: ConditionPrice[] = [
    { condition: 'nm', label: 'Near Mint', price: activePrices?.nm },
    { condition: 'lp', label: 'Light Play', price: activePrices?.lp },
    { condition: 'mp', label: 'Mod. Play', price: activePrices?.mp },
    { condition: 'hp', label: 'Heavy Play', price: activePrices?.hp },
    { condition: 'dm', label: 'Damaged', price: activePrices?.dm },
  ].filter((c) => c.price != null);

  const gradedPrices = [
    { grade: 'PSA 10', price: activeGradedPrices?.psa10 },
    { grade: 'PSA 9', price: activeGradedPrices?.psa9 },
    { grade: 'CGC 10', price: activeGradedPrices?.cgc10 },
    { grade: 'CGC 9.5', price: activeGradedPrices?.cgc9_5 },
  ].filter((g) => g.price != null);

  const getDisplayPrice = (price: number): string => {
    if (mode === 'buy') {
      return formatPrice(calculateBuyPrice(price, activePercent));
    }
    return formatPrice(price);
  };

  const getCartPrice = (price: number): number => {
    return mode === 'buy' ? calculateBuyPrice(price, buyPercent) : price;
  };

  const handleAddToCart = () => {
    if (!onAddToCart) return;
    const conditionData = conditions.find((c) => c.condition === selectedCondition);
    if (!conditionData?.price) return;
    const price = getCartPrice(conditionData.price);
    onAddToCart({
      cardId: card.id,
      cardName: card.name,
      setName: card.set_name,
      cardNumber: card.card_number,
      condition: selectedCondition,
      originalPrice: price,
      finalPrice: price,
      edition: selectedEdition ?? undefined,
    });
    setAddedConfirmation(
      `Added ${card.name} (${selectedCondition.toUpperCase()}) — ${formatPrice(price)}`
    );
    setTimeout(() => setAddedConfirmation(null), 2000);
  };

  // Check if this is a high-value card
  const nmPrice = activePrices?.nm ?? card.price;
  const valueTier = getValueTier(nmPrice);
  const isHighValue = valueTier !== 'standard';

  return (
    <div className="h-full overflow-auto p-6">
      {/* Card header */}
      <div className="flex gap-6">
        {/* Image - larger for tablet */}
        <div
          className={cn(
            'w-40 flex-shrink-0 rounded-lg overflow-hidden',
            isHighValue && 'ring-2 ring-tertiary'
          )}
        >
          {card.image_url ? (
            <CardImage
              src={card.image_url}
              alt={card.name}
              className="w-full rounded-lg shadow-md"
            />
          ) : (
            <div className="flex aspect-[3/4] items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
              No image
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 pt-2">
          <h2 className="text-xl font-bold text-foreground">{card.name}</h2>
          {card.en_name && card.en_name !== card.name && (
            <p className="text-sm text-muted-foreground">{card.en_name}</p>
          )}
          <p className="mt-1 text-sm text-muted-foreground">
            {card.set_name} · #{card.card_number}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {card.rarity && (
              <span className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {card.rarity}
              </span>
            )}
            {isHighValue && (
              <Badge variant="tertiary">
                {valueTier === 'ultra' ? 'High Value' : '$50+'}
              </Badge>
            )}
          </div>

          {/* Mode indicator — quote mode shows a percent stepper instead of
              the static badge so the operator can adjust the offer on the fly. */}
          {showQuoteStepper ? (
            <div className="mt-4">
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Quote at
              </h3>
              <div className="flex flex-wrap gap-2">
                {QUOTE_PERCENT_OPTIONS.map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setActivePercent(pct)}
                    className={cn(
                      'rounded-full px-3 py-1.5 text-sm font-semibold transition-all duration-200 ease-emphasized-decelerate',
                      activePercent === pct
                        ? 'bg-secondary text-secondary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <Badge variant={mode === 'buy' ? 'tertiary' : 'default'} className="px-3 py-1 text-sm">
                {mode === 'buy' ? `Buy Offer (${buyPercent}%)` : 'Sell Price'}
              </Badge>
            </div>
          )}

          {/* Add to Compare button */}
          {onAddToCompare && (
            <button
              onClick={() => onAddToCompare(card)}
              disabled={!canAddToComparison}
              className={cn(
                'mt-4 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ease-emphasized-decelerate',
                isInComparison
                  ? 'bg-primary/12 text-primary cursor-default'
                  : canAddToComparison
                    ? 'bg-muted text-foreground hover:bg-outline/20'
                    : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
              )}
            >
              <Plus className="h-4 w-4" />
              {isInComparison ? 'Added to Compare' : 'Add to Compare'}
            </button>
          )}
        </div>
      </div>

      {/* Price History Chart - larger for tablet. Defaults to 7D in quote
          mode (recent movement matters for live quotes), 30D otherwise. */}
      <div className="mt-6">
        <PriceHistoryChart
          cardName={card.name}
          setName={card.set_name}
          cardNumber={card.card_number}
          height={200}
          initialDuration={isQuoteMode ? '7d' : '30d'}
        />
      </div>

      {/* Edition toggle (vintage sets only) */}
      {card.has_editions && card.available_editions && card.available_editions.length > 1 && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Edition</h3>
          <div className="flex rounded-full bg-muted p-1">
            {card.available_editions.map((edition) => (
              <button
                key={edition}
                onClick={() => {
                  setSelectedEdition(edition);
                  track('edition_toggled', {
                    edition,
                    card_id: card.id,
                    surface: 'card_detail_panel',
                  });
                }}
                className={cn(
                  'flex-1 rounded-full px-3 py-2 text-sm font-medium transition-all duration-200 ease-emphasized-decelerate',
                  selectedEdition === edition
                    ? edition === '1st'
                      ? 'bg-amber-500 text-black shadow-sm'
                      : edition === 'shadowless'
                        ? 'bg-slate-400 text-slate-900 shadow-sm'
                        : 'bg-secondary text-secondary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {getEditionFullName(edition)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Condition prices */}
      {conditions.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Raw Prices
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {conditions.map((c) => (
              <button
                key={c.condition}
                type="button"
                onClick={() => {
                  setSelectedCondition(c.condition);
                  track('condition_selected', {
                    condition: c.condition,
                    card_id: card.id,
                    surface: 'card_detail_panel',
                    mode,
                  });
                }}
                className={cn(
                  'flex items-center justify-between rounded-lg px-3 py-2 text-left transition-all',
                  selectedCondition === c.condition
                    ? mode === 'buy'
                      ? 'bg-muted ring-2 ring-tertiary'
                      : 'bg-muted ring-2 ring-primary'
                    : 'bg-muted'
                )}
              >
                <span className="text-sm text-muted-foreground">{c.label}</span>
                <span
                  className={cn(
                    'font-semibold',
                    mode === 'buy' ? 'text-tertiary' : 'text-primary'
                  )}
                >
                  {getDisplayPrice(c.price!)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Graded prices */}
      {gradedPrices.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Graded Prices
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {gradedPrices.map((g) => (
              <div
                key={g.grade}
                className="flex items-center justify-between rounded-lg bg-tertiary/12 px-3 py-2"
              >
                <span className="text-sm text-tertiary">{g.grade}</span>
                <span className="font-semibold text-tertiary">
                  {getDisplayPrice(g.price!)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inventory status */}
      <div className="mt-6">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
          <Package className="h-4 w-4" />
          Inventory Status
        </h3>
        {isLoadingInventory ? (
          <div className="flex items-center justify-center rounded-lg bg-muted py-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-outline border-t-primary" />
          </div>
        ) : inventoryError ? (
          <div className="rounded-lg bg-yellow-50 px-3 py-2 text-center text-sm text-yellow-700">
            {inventoryError}
          </div>
        ) : inventory.length > 0 ? (
          <div className="space-y-2">
            {inventory.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between rounded-lg bg-green-50 px-3 py-2"
              >
                <div>
                  <span className="text-sm font-medium text-green-700">
                    {item.condition}
                    {item.is_graded && ` - ${item.grading_company} ${item.grade}`}
                  </span>
                  <span className="ml-2 text-xs text-green-700/70">
                    x{item.quantity}
                  </span>
                </div>
                {item.price && (
                  <span className="text-sm font-semibold text-green-700">
                    {formatPrice(item.price)}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-muted px-3 py-2 text-center text-sm text-muted-foreground">
            Not in stock
          </div>
        )}
      </div>

      {/* Add to Cart button */}
      {onAddToCart && conditions.length > 0 && (
        <div className="mt-6">
          {addedConfirmation && (
            <div className="mb-2 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
              <Check className="h-4 w-4 flex-shrink-0" />
              {addedConfirmation}
            </div>
          )}
          <Button
            type="button"
            onClick={handleAddToCart}
            disabled={!conditions.some((c) => c.condition === selectedCondition)}
            variant={mode === 'buy' ? 'tertiary' : 'default'}
            className="w-full"
          >
            <ShoppingCart className="h-4 w-4" />
            Add to Cart &mdash;{' '}
            {(() => {
              const cd = conditions.find((c) => c.condition === selectedCondition);
              return cd ? formatPrice(getCartPrice(cd.price!)) : '--';
            })()}
          </Button>
        </div>
      )}
    </div>
  );
}
